import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import ChevronLeftSvg from '^/assets/icons/photo-request/chevron-left.svg';
import RenameSvg from '^/assets/icons/photo-request/rename.svg';
import DeleteSvg from '^/assets/icons/photo-request/trash.svg';
import VerticalKebabSvg from '^/assets/icons/photo-request/vertical-kebab.svg';
import { useL10n } from '^/hooks';
import {
  useDeleteV2BAlbumMutation,
  useRenameV2BAlbumMutation,
} from '^/hooks/api/viewpoint-capture/useViewpoint';
import theme from '^/theme';
import * as T from '^/types';
import styled from 'styled-components';
import { DropdownMenu } from '../DropdownMenu';
import EditableTitle from '../EditableTitle';
import Text from './text';
import RequestCountBadge from '^/components/atoms/RequestCountBadge';
import { useViewpointStore } from '^/store/viewpointStore';
const Header = styled.div({
  padding: '16px 20px',
  borderBottom: `1px solid ${theme.colors.coolGray[100]}`,
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const BackButton = styled.button({
  color: theme.colors.coolGray[500],
  fontSize: '14px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  backgroundColor: 'transparent',
  '&:disabled': {
    opacity: 0.5,
  },
});

const TitleContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  maxWidth: '100%',
});

const MissionTitle = styled.span({
  fontSize: '14px',
  color: theme.colorTokens.textCoolDarker,
  whiteSpace: 'nowrap',
  overflowX: 'clip',
  textOverflow: 'ellipsis',
});

interface Props {
  selectedViewpointAlbum: T.PhotoAlbumbyId;
}
export function ViewpointPhotoListHeader({ selectedViewpointAlbum }: Props) {
  const [l10n] = useL10n();
  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState(selectedViewpointAlbum?.title);
  const navigate = useNavigate();

  const { mutate: renamePhotoAlbum } = useRenameV2BAlbumMutation();
  const { mutate: deletePhotoAlbum } = useDeleteV2BAlbumMutation();
  const isViewpointUploading = useViewpointStore(s => s.isViewpointUploading);

  const handleEdit = () => {
    if (selectedViewpointAlbum.status !== T.RequestPhotoStatus.PENDING) {
      return;
    }
    setIsEditing(true);
    setEditedTitle(selectedViewpointAlbum?.title);
  };

  const handleRename = () => {
    renamePhotoAlbum({
      photoAlbumId: selectedViewpointAlbum.id,
      newTitle: editedTitle,
    });
    setIsEditing(false);
  };

  const handleDelete = () => {
    deletePhotoAlbum(
      {
        photoAlbumId: selectedViewpointAlbum.id,
      },
      {
        onSuccess: () => {
          navigate(-1);
        },
      }
    );
  };

  const dropdownOptions: T.DropdownOption[] = [
    {
      label: l10n(Text.rename),
      action: handleEdit,
      preIcon: <RenameSvg />,
    },
    {
      label: l10n(Text.delete),
      action: handleDelete,
      preIcon: <DeleteSvg />,
    },
  ];

  return (
    <Header>
      <BackButton onClick={() => navigate(-1)} disabled={isViewpointUploading}>
        <ChevronLeftSvg />
      </BackButton>
      <TitleContainer>
        {isEditing ? (
          <EditableTitle
            editedTitle={editedTitle}
            isEditing={isEditing}
            setEditedTitle={setEditedTitle}
            setIsEditing={setIsEditing}
            onSave={handleRename}
          />
        ) : (
          <MissionTitle onDoubleClick={handleEdit}>{selectedViewpointAlbum?.title}</MissionTitle>
        )}
      </TitleContainer>
      <RequestCountBadge count={selectedViewpointAlbum?.photosCount} />
      {selectedViewpointAlbum?.status === T.RequestPhotoStatus.PENDING && (
        <DropdownMenu
          options={dropdownOptions}
          triggerStyles={{
            marginLeft: 'auto',
          }}
        >
          <VerticalKebabSvg />
        </DropdownMenu>
      )}
    </Header>
  );
}
